/* eslint eslint-comments/no-use: off, i18n-text/no-en: off */
import {on} from 'delegated-events'
import {parseHTML} from '@github-ui/parse-html'
import {SELECTORS} from './types'
import type {SelectedServer, ServerEntry} from './types'
import {getDialogState, resetDialogState as resetState} from './state'
import {createServersEmptyState, createServerRow, createAllowlistRow, createAllowlistTable} from './templates'

// Hardcoded demo values for hidden UI
const DEMO_AVATAR_URL = 'https://avatars.githubusercontent.com/u/1?v=4'
const DEMO_USER_NAME = 'octocat'

type SelectPanelEl = HTMLElement & {
  items?: HTMLElement[]
  checkItem?: (item: HTMLElement) => void
  uncheckItem?: (item: HTMLElement) => void
}

function showSuccessBanner(): void {
  const banner = document.getElementById('allowlist-success-banner')
  if (banner) {
    banner.hidden = false
    requestAnimationFrame(() => {
      banner.scrollIntoView({behavior: 'smooth', block: 'start'})
    })
  }
}

function getDialogController(element: Element): Element | null {
  // First try to find the controller as an ancestor
  const controllerFromAncestor = element.closest('[data-controller="allowlist-dialog"]')
  if (controllerFromAncestor) return controllerFromAncestor

  // If not found, the element might be in the header/footer (outside the controller div)
  // In that case, find the dialog first, then find the controller within it
  const dialog = element.closest('#create-allowlist-dialog') || element.closest('[id^="edit-allowlist-dialog-"]')
  if (dialog) {
    return dialog.querySelector('[data-controller="allowlist-dialog"]')
  }
  return null
}

function getDialog(element: Element): Element | null {
  // Support both create and edit dialogs
  return element.closest('[id$="-allowlist-dialog"], [id^="edit-allowlist-dialog-"]')
}

function showPage(controller: Element, page: 'main' | 'add-server'): void {
  const mainPage = controller.querySelector<HTMLElement>('[data-allowlist-dialog-target="mainPage"]')
  const addServerPage = controller.querySelector<HTMLElement>('[data-allowlist-dialog-target="addServerPage"]')

  // Get the dialog to find header/footer elements
  const dialog = getDialog(controller)

  // Header elements
  const backButton = dialog?.querySelector<HTMLElement>('[data-allowlist-dialog-target="backButton"]')
  const dialogTitle = dialog?.querySelector('[data-allowlist-dialog-target="dialogTitle"]')

  // Footer elements
  const mainFooter = dialog?.querySelector<HTMLElement>('[data-allowlist-dialog-target="mainFooter"]')
  const addServerFooter = dialog?.querySelector<HTMLElement>('[data-allowlist-dialog-target="addServerFooter"]')

  if (page === 'main') {
    // Show main page content
    if (mainPage) mainPage.hidden = false
    if (addServerPage) addServerPage.hidden = true

    // Update header - hide back button, restore original title
    if (backButton) backButton.hidden = true
    if (dialogTitle) {
      const originalTitle = dialogTitle.getAttribute('data-original-title') || dialogTitle.textContent || ''
      dialogTitle.textContent = originalTitle
    }

    // Update footer - show main footer, hide add-server footer
    if (mainFooter) mainFooter.hidden = false
    if (addServerFooter) addServerFooter.hidden = true

    // Reset selection when going back
    resetServerSelection(controller)
  } else {
    // Show add-server page content
    if (mainPage) mainPage.hidden = true
    if (addServerPage) addServerPage.hidden = false

    // Cache the pristine SelectPanel HTML on first visit so we can
    // replace the element with a fresh copy on subsequent resets.
    if (!serverPanelTemplates.has(controller)) {
      const panel = controller.querySelector('[data-allowlist-dialog-target="serverSelectPanel"]')
      if (panel) {
        serverPanelTemplates.set(controller, panel.outerHTML)
      }
    }

    // Update header - show back button, save original title and change to Add server
    if (backButton) backButton.hidden = false
    if (dialogTitle) {
      if (!dialogTitle.getAttribute('data-original-title')) {
        dialogTitle.setAttribute('data-original-title', dialogTitle.textContent || '')
      }

      dialogTitle.textContent = 'Add server'
    }

    // Update footer - hide main footer, show add-server footer
    if (mainFooter) mainFooter.hidden = true
    if (addServerFooter) addServerFooter.hidden = false
  }
}

// Cache the original outerHTML of each server SelectPanel so we can replace
// the element with a fresh instance on reset (clearing all private state).
const serverPanelTemplates = new WeakMap<Element, string>()

function resetServerSelection(controller: Element): void {
  const state = getDialogState()
  state.setCurrentSelectedServer(null)

  // Replace the SelectPanel element with a fresh copy to clear all internal state
  const selectPanel = controller.querySelector('[data-allowlist-dialog-target="serverSelectPanel"]')
  if (selectPanel) {
    const template = serverPanelTemplates.get(controller)
    if (template) {
      // Insert the fresh panel in the same position, then remove the old one
      selectPanel.insertAdjacentHTML('afterend', template)
      selectPanel.remove()
    } else {
      // First time — just reset the button text
      const button = selectPanel.querySelector('button')
      if (button) {
        const buttonText = button.querySelector('.Button-label')
        if (buttonText) {
          buttonText.textContent = 'Select server name'
        }
      }
    }
  }

  // Clear hidden inputs
  const idInput = controller.querySelector<HTMLInputElement>('[data-allowlist-dialog-target="selectedServerId"]')
  const nameInput = controller.querySelector<HTMLInputElement>('[data-allowlist-dialog-target="selectedServerName"]')
  const urlInput = controller.querySelector<HTMLInputElement>('[data-allowlist-dialog-target="selectedServerUrl"]')
  if (idInput) idInput.value = ''
  if (nameInput) nameInput.value = ''
  if (urlInput) urlInput.value = ''

  // Hide and clear connection methods
  hideConnectionMethods(controller)
}

function hideConnectionMethods(controller: Element): void {
  const connectionMethods = controller.querySelector<HTMLElement>('[data-allowlist-dialog-target="connectionMethods"]')
  if (connectionMethods) connectionMethods.hidden = true
  const list = controller.querySelector('[data-allowlist-dialog-target="connectionMethodsList"]')
  if (list) list.textContent = ''
}

// Parse ServerEntry[] from a container of server-rendered Primer checkboxes.
function parseCheckboxEntries(container: Element): ServerEntry[] {
  const checkboxes = container.querySelectorAll<HTMLInputElement>('input[type="checkbox"][name="server_entries[]"]')
  const entries: ServerEntry[] = []

  for (const checkbox of checkboxes) {
    try {
      const parsed = JSON.parse(checkbox.value) as {type: string; transport_id: number}
      const label = checkbox.closest('.FormControl-checkbox-wrap')?.querySelector('.FormControl-label')
      entries.push({
        id: parsed.transport_id,
        type: parsed.type as 'remote' | 'package',
        displayName: label?.textContent?.trim() || '',
        selected: checkbox.checked,
      })
    } catch {
      // skip malformed entries
    }
  }

  return entries
}

// Fetch server-rendered connection method checkboxes from the connection_methods endpoint.
// Both create and edit flows use the same endpoint — it returns a Primer CheckBoxGroup HTML fragment.
async function fetchAndRenderEntries(controller: Element, serverId: string): Promise<void> {
  const container = controller.querySelector<HTMLElement>('[data-allowlist-dialog-target="addServerContainer"]')
  if (!container) return

  // Track the latest fetch per-container to avoid rendering stale responses
  container.setAttribute('data-latest-fetch-server-id', serverId)

  const baseUrl = container.getAttribute('data-connection-methods-url')
  if (!baseUrl) return

  const connectionMethods = controller.querySelector<HTMLElement>('[data-allowlist-dialog-target="connectionMethods"]')
  const list = controller.querySelector<HTMLElement>('[data-allowlist-dialog-target="connectionMethodsList"]')
  if (!connectionMethods || !list) return

  // Build URL based on flow (edit vs create)
  const url = new URL(baseUrl, window.location.origin)
  url.searchParams.set('server_id', serverId)

  const dialog = getDialog(controller)
  const allowlistId = dialog?.getAttribute('data-allowlist-id') || ''

  if (allowlistId) {
    // Edit flow: server_entries fetched from MCP Registry API on the backend
    url.searchParams.set('allowlist_id', allowlistId)
  } else {
    // Create flow: pass catalog data so the backend can build entries
    const selectPanel = controller.querySelector('[data-allowlist-dialog-target="serverSelectPanel"]')
    const selectedItem =
      selectPanel?.querySelector('[aria-selected="true"]') || selectPanel?.querySelector('[aria-checked="true"]')
    const contentEl = selectedItem?.querySelector('[data-server-name]') || selectedItem

    const remotesJson = contentEl?.getAttribute('data-server-remotes')
    const packagesJson = contentEl?.getAttribute('data-server-packages')

    if (remotesJson) url.searchParams.set('remotes', remotesJson)
    if (packagesJson) url.searchParams.set('packages', packagesJson)
  }

  // Clear stale connection method checkboxes before loading new ones
  const loadingSpinner = controller.querySelector<HTMLElement>(
    '[data-allowlist-dialog-target="connectionMethodsLoading"]',
  )
  list.textContent = ''
  if (loadingSpinner) list.appendChild(loadingSpinner)

  // Show loading state while fetching connection methods
  list.setAttribute('aria-busy', 'true')
  if (loadingSpinner) loadingSpinner.hidden = false
  connectionMethods.hidden = false

  try {
    const response = await fetch(url.toString(), {
      headers: {Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest'},
    })

    // Ignore stale response if the user selected a different server while we were fetching
    if (container.getAttribute('data-latest-fetch-server-id') !== serverId) return

    if (!response.ok) {
      connectionMethods.hidden = true
      return
    }

    const html = await response.text()

    // Re-check after async text() call
    if (container.getAttribute('data-latest-fetch-server-id') !== serverId) return

    if (!html.trim()) {
      connectionMethods.hidden = true
      list.textContent = ''
      return
    }

    // Use parseHTML for safe sanitized DOM insertion
    list.textContent = ''
    list.appendChild(parseHTML(document, html))
    list.setAttribute('data-server-id', serverId)
    connectionMethods.hidden = false

    // Populate state from the server-rendered checkboxes
    getDialogState().setServerEntries(serverId, parseCheckboxEntries(list))
  } catch {
    connectionMethods.hidden = true
  } finally {
    // Only clear loading state if this is still the latest request
    if (container.getAttribute('data-latest-fetch-server-id') === serverId) {
      list.setAttribute('aria-busy', 'false')
      if (loadingSpinner) loadingSpinner.hidden = true
    }
  }
}

function updateServersList(controller: Element): void {
  const state = getDialogState()
  const serversList = controller.querySelector('[data-mcp-target="servers-list"]')
  if (!serversList) return

  // Ensure screen readers announce list changes
  serversList.setAttribute('aria-live', 'polite')

  // Find the header
  const header = serversList.querySelector('.Box-header')
  const existingRows = serversList.querySelectorAll('.Box-row')

  // Remove existing rows
  for (const row of existingRows) {
    row.remove()
  }

  // Remove any previous removed-server hidden inputs
  const prevRemovedInputs = serversList.querySelectorAll('[data-removed-server-input]')
  for (const input of prevRemovedInputs) {
    input.remove()
  }

  if (state.addedServers.length === 0) {
    // Show empty state
    const emptyState = createServersEmptyState()
    header?.after(emptyState)
  } else {
    // Add rows for each server (in reverse order so they appear correctly after header)
    const servers = [...state.addedServers]
    for (let i = servers.length - 1; i >= 0; i--) {
      const server = servers[i]
      if (!server) continue
      const entries = state.getServerEntries(server.id)
      const row = createServerRow(server, i, entries.length > 0 ? entries : undefined)
      header?.after(row)
    }
  }

  // Render hidden inputs for servers that were removed (so the backend knows to delete them)
  for (let i = 0; i < state.removedServers.length; i++) {
    const removed = state.removedServers[i]
    if (!removed) continue
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = `allowlist[removed_servers][${i}][public_id]`
    input.value = removed.id
    input.setAttribute('data-removed-server-input', 'true')
    serversList.appendChild(input)
  }

  // Update server count in header if needed
  const headerText = header?.querySelector('span')
  if (headerText && state.addedServers.length > 0) {
    headerText.textContent = `Servers (${state.addedServers.length})`
  } else if (headerText) {
    headerText.textContent = 'Servers'
  }
}

function updateOrgButtonText(
  controller: Element,
  selectedOrgs: ReadonlyArray<{name: string}>,
  totalOrgs: number,
): void {
  const orgSelectText = controller.querySelector('[data-allowlist-dialog-target="orgSelectText"]')
  if (!orgSelectText) return
  if (selectedOrgs.length === 0) {
    orgSelectText.textContent = 'Unassigned'
  } else if (selectedOrgs.length === totalOrgs) {
    orgSelectText.textContent = 'All Organizations'
  } else if (selectedOrgs.length === 1) {
    const firstOrg = selectedOrgs[0]
    orgSelectText.textContent = firstOrg ? firstOrg.name : 'Unassigned'
  } else {
    orgSelectText.textContent = `${selectedOrgs.length} organizations`
  }
}

function resetDialogFormState(): void {
  resetState()

  const dialog = document.getElementById('create-allowlist-dialog')
  if (!dialog) return

  const controller = dialog.querySelector(SELECTORS.CONTROLLER)
  if (!controller) return

  showPage(controller, 'main')

  // Reset org button text — default to unassigned (no orgs selected)
  updateOrgButtonText(controller, [], 0)

  // Clear hidden inputs
  const orgHiddenInputs = controller.querySelector('[data-allowlist-dialog-target="orgHiddenInputs"]')
  if (orgHiddenInputs) {
    orgHiddenInputs.textContent = ''
  }

  // Uncheck all org items in the SelectPanel so the UI matches "Unassigned"
  const selectPanelEl: SelectPanelEl | null = dialog.querySelector('[id*="allowlist-org-select-panel"]')
  if (selectPanelEl?.uncheckItem && selectPanelEl?.items) {
    for (const item of selectPanelEl.items) {
      selectPanelEl.uncheckItem(item)
    }
  } else {
    // Fallback if custom element not yet upgraded — clear both aria-selected and
    // aria-checked since Primer SelectPanel uses aria-checked for multi-select variants.
    const allItems = dialog.querySelectorAll('[id*="allowlist-org-select-panel"] [role="option"]')
    for (const item of allItems) {
      item.setAttribute('aria-selected', 'false')
      item.setAttribute('aria-checked', 'false')
    }
  }

  // Also reset form fields
  const nameInput = dialog.querySelector<HTMLInputElement>('input[name="allowlist[name]"]')
  const descInput = dialog.querySelector<HTMLTextAreaElement>('textarea[name="allowlist[description]"]')
  if (nameInput) nameInput.value = ''
  if (descInput) descInput.value = ''

  // Reset the servers list UI
  updateServersList(controller)
}

// ============================================================================
// Event Handlers - registered at module load time (top-level scope)
// ============================================================================

// Show add server page and load initial server list
on(
  'click',
  '[data-allowlist-action="showAddServerPage"], [data-action="click->allowlist-dialog#showAddServerPage"]',
  function (event) {
    event.preventDefault()
    const controller = getDialogController(event.currentTarget as Element)
    if (controller) {
      // Lazy-hydrate edit dialog state if dialog:open didn't fire
      if (controller.hasAttribute('data-servers') && !controller.hasAttribute('data-state-hydrated')) {
        hydrateEditDialogState(controller)
      }

      showPage(controller, 'add-server')
    }
  },
)

// Show main page (back button or cancel on add server page)
on(
  'click',
  '[data-allowlist-action="showMainPage"], [data-action="click->allowlist-dialog#showMainPage"]',
  function (event) {
    event.preventDefault()
    const controller = getDialogController(event.currentTarget as Element)
    if (controller) {
      showPage(controller, 'main')
    }
  },
)

// Handle server selection from SelectPanel dropdown
on('itemActivated', '[data-allowlist-dialog-target="serverSelectPanel"]', function (event) {
  const customEvent = event as CustomEvent
  const target = event.currentTarget as HTMLElement
  const controller = getDialogController(target)
  if (!controller) return

  const detail = customEvent.detail
  const item = detail?.item as HTMLElement | undefined
  if (!item) return

  const state = getDialogState()

  // Primer SelectPanel puts `value` on the <li> as data-value,
  // but content_arguments data attrs are on a child element.
  const contentEl = item.querySelector('[data-server-name]') || item
  const serverId = contentEl.getAttribute('data-server-id') || item.getAttribute('data-value') || ''
  const serverName =
    contentEl.getAttribute('data-server-name') || item.querySelector('[data-label-text]')?.textContent?.trim() || ''
  const serverUrl = contentEl.getAttribute('data-server-url') || ''

  state.setCurrentSelectedServer({id: serverId, name: serverName, url: serverUrl})

  // Update hidden inputs
  const idInput = controller.querySelector<HTMLInputElement>('[data-allowlist-dialog-target="selectedServerId"]')
  const nameInput = controller.querySelector<HTMLInputElement>('[data-allowlist-dialog-target="selectedServerName"]')
  const urlInput = controller.querySelector<HTMLInputElement>('[data-allowlist-dialog-target="selectedServerUrl"]')
  if (idInput) idInput.value = serverId
  if (nameInput) nameInput.value = serverName
  if (urlInput) urlInput.value = serverUrl

  // Fetch and render connection method checkboxes for the selected server
  fetchAndRenderEntries(controller, serverId)
})

// Add selected server to allowlist
on(
  'click',
  '[data-allowlist-action="addSelectedServer"], [data-action="click->allowlist-dialog#addSelectedServer"]',
  function (event) {
    event.preventDefault()
    const controller = getDialogController(event.currentTarget as Element)
    const state = getDialogState()
    if (!controller || !state.currentSelectedServer) return

    state.addServer(state.currentSelectedServer)
    updateServersList(controller)

    // Go back to main page
    showPage(controller, 'main')
  },
)

// Remove server from allowlist
on('click', '.js-remove-server-from-allowlist', function (event) {
  event.preventDefault()
  const target = event.currentTarget as HTMLElement
  const controller = getDialogController(target)

  // Lazy-hydrate edit dialog state if dialog:open didn't fire
  if (controller && controller.hasAttribute('data-servers') && !controller.hasAttribute('data-state-hydrated')) {
    hydrateEditDialogState(controller)
  }

  const index = parseInt(target.getAttribute('data-server-index') || '-1', 10)
  const state = getDialogState()
  if (state.removeServerAtIndex(index)) {
    if (controller) {
      updateServersList(controller)
    }
  }
})

// Handle connection method checkbox changes (Primer CheckBoxGroup)
on('change', '[data-allowlist-dialog-target="connectionMethodsList"] input[type="checkbox"]', function (event) {
  const checkbox = event.currentTarget as HTMLInputElement
  const list = checkbox.closest('[data-allowlist-dialog-target="connectionMethodsList"]')
  const serverId = list?.getAttribute('data-server-id')
  if (!serverId) return

  try {
    const parsed = JSON.parse(checkbox.value) as {type: string; transport_id: number}
    getDialogState().setEntrySelected(serverId, parsed.transport_id, checkbox.checked)
  } catch {
    // skip malformed checkbox values
  }
})

// Save allowlist and add to table
on(
  'click',
  '[data-allowlist-action="saveAllowlist"], [data-action="click->allowlist-dialog#saveAllowlist"]',
  function (event) {
    event.preventDefault()

    const dialog = document.getElementById('create-allowlist-dialog')
    if (!dialog) return

    // Get form values
    const nameInput = dialog.querySelector<HTMLInputElement>('input[name="allowlist[name]"]')
    const descInput = dialog.querySelector<HTMLTextAreaElement>('textarea[name="allowlist[description]"]')

    const name = nameInput?.value?.trim() || ''
    const description = descInput?.value?.trim() || ''

    // Require name
    if (!name) {
      nameInput?.focus()
      return
    }

    const state = getDialogState()
    const serverCount = state.addedServers.length
    const orgNames = state.selectedOrganizations.map(org => org.name)
    const avatarUrl = DEMO_AVATAR_URL
    const userName = DEMO_USER_NAME

    // Add new row to the table
    const table = document.querySelector('[data-mcp-target="allowlists-table"]')
    const emptyState = document.querySelector('[data-mcp-target="allowlists-empty-state"]')

    if (table) {
      const newRow = createAllowlistRow(name, description, serverCount, orgNames, avatarUrl, userName)

      // Insert at the beginning of the Box-body list
      const body = table.querySelector('ul.Box-body')
      if (body) {
        body.prepend(newRow)
      } else {
        // Fallback: insert after the header
        const header = table.querySelector('.Box-header')
        if (header) {
          header.after(newRow)
        }
      }
    } else if (emptyState) {
      // If showing empty state, create the table structure
      const tableContainer = createAllowlistTable(name, description, serverCount, orgNames, avatarUrl, userName)
      emptyState.replaceWith(tableContainer)
    }

    // Reset form
    if (nameInput) nameInput.value = ''
    if (descInput) descInput.value = ''
    resetState()

    // Reset org select button text
    const controller = dialog.querySelector('[data-controller="allowlist-dialog"]')
    if (controller) {
      updateOrgButtonText(controller, [], 0)
      const orgHiddenInputs = controller.querySelector('[data-allowlist-dialog-target="orgHiddenInputs"]')
      if (orgHiddenInputs) {
        orgHiddenInputs.textContent = ''
      }
    }

    // Show success banner
    showSuccessBanner()

    // Close dialog
    const closeButton = dialog.querySelector('[data-close-dialog-id="create-allowlist-dialog"]') as HTMLElement
    closeButton?.click()
  },
)

// Reset state when dialog is closed
on('click', '[data-close-dialog-id="create-allowlist-dialog"]', function () {
  resetDialogFormState()
})

// Reset state when dialog opens (handles re-opens with stale state).
// Guard against dialog:open events from child SelectPanels (their dropdown
// also fires dialog:open which bubbles up through the outer dialog).
on('dialog:open', SELECTORS.DIALOG, function (event) {
  if ((event.target as Element).closest('select-panel')) return
  resetDialogFormState()
})

// Hydrate JS state from the data-servers and data-assigned-org-ids JSON attributes
// on an edit dialog controller. Marks the controller so it only hydrates once per open.
function hydrateEditDialogState(controller: Element): void {
  const state = getDialogState()
  state.reset()

  const serversJson = controller.getAttribute('data-servers')
  let servers: Array<{public_id?: string; name?: string; url?: string}> = []
  if (serversJson) {
    try {
      servers = JSON.parse(serversJson)
    } catch {
      // Fall back to empty list if the attribute is missing or malformed
      servers = []
    }
  }
  const hydratedServers: SelectedServer[] = servers
    .filter(s => s.public_id || s.name)
    .map(s => ({id: s.public_id || '', name: s.name || '', url: s.url || ''}))

  for (const server of hydratedServers) {
    state.addServer(server)
  }
  state.setOriginalServers(hydratedServers)

  // Restore org selections using the SelectPanel's public API so internal state stays in sync.
  // Empty assignedOrgIds means "unassigned" (no orgs selected).
  const dialog = getDialog(controller)
  if (dialog) {
    const selectPanelEl = dialog.querySelector('[id*="allowlist-org-select-panel"]') as SelectPanelEl
    if (selectPanelEl?.checkItem && selectPanelEl?.uncheckItem && selectPanelEl?.items) {
      const assignedOrgIdsJson = controller.getAttribute('data-assigned-org-ids')
      let assignedOrgIds: string[] = []
      if (assignedOrgIdsJson) {
        try {
          assignedOrgIds = JSON.parse(assignedOrgIdsJson).map(String)
        } catch {
          assignedOrgIds = []
        }
      }

      for (const item of selectPanelEl.items) {
        selectPanelEl.uncheckItem(item)
      }
      for (const item of selectPanelEl.items) {
        const contentEl = item.querySelector('[data-value]')
        const orgId = contentEl?.getAttribute('data-value') || ''
        if (assignedOrgIds.includes(orgId)) {
          selectPanelEl.checkItem(item)
        }
      }

      // Update button text to match restored selection
      updateOrgButtonText(controller, state.selectedOrganizations, selectPanelEl.items.length)
    }
  }

  controller.setAttribute('data-state-hydrated', 'true')

  // Lazy-fetch entries for each existing server using the connection_methods HTML endpoint
  const container = controller.querySelector<HTMLElement>('[data-allowlist-dialog-target="addServerContainer"]')
  const baseUrl = container?.getAttribute('data-connection-methods-url')
  const allowlistIdForEntries = dialog?.getAttribute('data-allowlist-id') || ''

  if (baseUrl && allowlistIdForEntries) {
    const serversToFetch = hydratedServers.filter(server => server.id)

    const hydrateServersList = controller.querySelector<HTMLElement>('[data-mcp-target="servers-list"]')
    if (hydrateServersList) hydrateServersList.setAttribute('aria-busy', 'true')
    ;(async () => {
      try {
        const fetchPromises = serversToFetch.map(async server => {
          const url = new URL(baseUrl, window.location.origin)
          url.searchParams.set('allowlist_id', allowlistIdForEntries)
          url.searchParams.set('server_id', server.id)

          try {
            const response = await fetch(url.toString(), {
              headers: {Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest'},
            })
            const html = response.ok ? await response.text() : null
            if (!html) return
            const tempDiv = document.createElement('div')
            tempDiv.appendChild(parseHTML(document, html))
            state.setServerEntries(server.id, parseCheckboxEntries(tempDiv))
          } catch {
            // Silently ignore — entries will just not show
          }
        })

        // Batch: re-render the server list once after all fetches complete
        await Promise.allSettled(fetchPromises)
        updateServersList(controller)
      } catch {
        // Prevent unhandled promise rejection if something unexpected throws
      } finally {
        if (hydrateServersList) hydrateServersList.setAttribute('aria-busy', 'false')
      }
    })()
  }
}

// Reset edit dialogs to main page when opened.
// Guard against dialog:open from child SelectPanels.
on('dialog:open', '[id^="edit-allowlist-dialog-"]', function (event) {
  if ((event.target as Element).closest('select-panel')) return
  const dialog = event.currentTarget as Element
  const controller = dialog.querySelector('[data-controller="allowlist-dialog"]')
  if (controller) {
    showPage(controller, 'main')
    controller.removeAttribute('data-state-hydrated')
    hydrateEditDialogState(controller)
    updateServersList(controller)
  }
})

// Reliable fallback: hydrate + re-render when the Edit button is clicked.
// Primer's dialog:open custom event doesn't always bubble to delegated-events,
// but the click on the trigger button is always captured.
on('click', '[data-show-dialog-id^="edit-allowlist-dialog-"]', function (event) {
  const dialogId = (event.currentTarget as HTMLElement).getAttribute('data-show-dialog-id')
  if (!dialogId) return
  const dialog = document.getElementById(dialogId)
  if (!dialog) return
  const controller = dialog.querySelector('[data-controller="allowlist-dialog"]')
  if (controller) {
    showPage(controller, 'main')
    controller.removeAttribute('data-state-hydrated')
    hydrateEditDialogState(controller)
    updateServersList(controller)
  }
})

// Handle organization selection from SelectPanel (matches both create and edit panels)
on('itemActivated', '[id*="allowlist-org-select-panel"]', function (event) {
  const customEvent = event as CustomEvent<{item: HTMLElement; checked: boolean}>
  const panel = event.currentTarget as Element

  const item = customEvent.detail?.item as HTMLElement | undefined
  if (!item) return

  // Data attributes are on an inner element when using content_arguments
  const contentElement = item.querySelector('[data-value]')
  const orgId = contentElement?.getAttribute('data-value') || ''
  const orgName = contentElement?.getAttribute('data-org-name') || item.textContent?.trim() || ''
  const isSelected = customEvent.detail.checked

  const state = getDialogState()

  if (isSelected) {
    state.addOrganization({id: orgId, name: orgName})
  } else {
    state.removeOrganization(orgId)
  }

  // Update button text
  const controller = getDialogController(panel)
  if (!controller) return

  const totalOrgs = panel.querySelectorAll('[role="option"]').length
  updateOrgButtonText(controller, state.selectedOrganizations, totalOrgs)

  // Update hidden inputs
  const orgHiddenInputs = controller.querySelector('[data-allowlist-dialog-target="orgHiddenInputs"]')
  if (orgHiddenInputs) {
    // Clear and rebuild hidden inputs
    orgHiddenInputs.textContent = ''
    for (const org of state.selectedOrganizations) {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'allowlist[organization_ids][]'
      input.value = org.id
      orgHiddenInputs.appendChild(input)
    }
  }
})
